import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, PenSquare, RefreshCw, Reply } from 'lucide-react';
import type { OwnerMailFolderDto, OwnerMailListDto, OwnerMailMessageDto } from '@his/shared';
import { downloadGet, get } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { formatBytes } from '@/lib/format-bytes';
import { useTranslation } from '@/lib/i18n';
import { cn, dateTime } from '@/lib/utils';
import { PageHeader } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { EMPTY_DRAFT, OwnerMailComposeDialog, type ComposeDraft } from './OwnerMailComposeDialog';
import { mailAttachmentPath, mailMessagePath, mailMessagesPath, quoteForReply, replySubject, senderLabel } from './mail-helpers';

const MAIL_KEY = ['owner-mail'] as const;
const INBOX: OwnerMailFolderDto = { path: 'INBOX', name: 'Inbox', special: 'Inbox' };

function Reader({ message, onReply }: { message: OwnerMailMessageDto; onReply: (message: OwnerMailMessageDto) => void }) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const unknown = t('owner_mail_unknown_sender');
  const addresses = (list: OwnerMailMessageDto['to']) => list.map((entry) => entry.address ?? entry.name).join(', ');

  const download = async (index: number, fileName: string) => {
    try {
      await downloadGet(mailAttachmentPath(message.folder, message.uid, index), fileName);
    } catch (error) {
      toast.error(t('owner_mail_attachment_failed'), describeError(error));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <h3 className="text-base font-medium">{message.subject || t('owner_mail_no_subject')}</h3>
          <p className="text-xs text-muted-foreground">{t('owner_mail_from_x', { sender: senderLabel(message.from, unknown) })}</p>
          <p className="text-xs text-muted-foreground">
            {t('owner_mail_to_x', { list: addresses(message.to) || '—' })}
            {message.cc.length > 0 ? ` · ${t('owner_mail_cc_x', { list: addresses(message.cc) })}` : ''}
          </p>
          {message.date && <p className="text-xs text-muted-foreground">{dateTime(message.date)}</p>}
        </div>
        <Button size="sm" variant="secondary" onClick={() => onReply(message)}>
          <Reply className="h-3.5 w-3.5" /> {t('owner_mail_reply')}
        </Button>
      </div>
      {message.tooLarge && <Alert variant="warning">{t('owner_mail_too_large')}</Alert>}
      {message.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {message.attachments.map((attachment) => (
            <Button
              key={attachment.index}
              size="sm"
              variant="outline"
              disabled={!attachment.downloadable}
              title={attachment.downloadable ? undefined : t('owner_mail_attachment_too_large')}
              onClick={() => void download(attachment.index, attachment.fileName)}
              aria-label={t('owner_mail_download_x', { name: attachment.fileName })}
            >
              <Paperclip className="h-3.5 w-3.5" /> {attachment.fileName} · {formatBytes(attachment.sizeBytes)} <Download className="h-3.5 w-3.5" />
            </Button>
          ))}
        </div>
      )}
      {message.html ? (
        <iframe
          title={t('owner_mail_message')}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={message.html}
          className="h-[55vh] w-full rounded-md border border-border bg-white"
        />
      ) : (
        <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm">{message.text}</pre>
      )}
    </div>
  );
}

export function OwnerMailPage() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [folder, setFolder] = useState(INBOX.path);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<ComposeDraft | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const folders = useQuery({
    queryKey: [...MAIL_KEY, 'folders'],
    queryFn: () => get<OwnerMailFolderDto[]>('/owner/mail/folders'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const list = useQuery({
    queryKey: [...MAIL_KEY, 'list', folder, page],
    queryFn: () => get<OwnerMailListDto>(mailMessagesPath(folder, page)),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const message = useQuery({
    queryKey: [...MAIL_KEY, 'message', folder, selected],
    queryFn: async () => {
      const opened = await get<OwnerMailMessageDto>(mailMessagePath(folder, selected ?? 0));
      queryClient.setQueryData<OwnerMailListDto>([...MAIL_KEY, 'list', folder, page], (old) =>
        old ? { ...old, messages: old.messages.map((entry) => (entry.uid === opened.uid ? { ...entry, seen: true } : entry)) } : old,
      );
      return opened;
    },
    enabled: selected !== null,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const refresh = async () => {
    setRefreshing(true);
    try {
      queryClient.setQueryData([...MAIL_KEY, 'list', folder, page], await get<OwnerMailListDto>(mailMessagesPath(folder, page, true)));
    } catch (error) {
      toast.error(t('owner_mail_refresh_failed'), describeError(error));
    } finally {
      setRefreshing(false);
    }
  };

  const reply = (original: OwnerMailMessageDto) =>
    setDraft({
      to: original.from?.address ?? '',
      cc: '',
      subject: replySubject(original.subject),
      text: quoteForReply(
        original.text,
        t('owner_mail_wrote', { when: original.date ? dateTime(original.date) : '—', sender: senderLabel(original.from, t('owner_mail_unknown_sender')) }),
      ),
      ...(original.messageId ? { inReplyTo: original.messageId } : {}),
    });

  const changeFolder = (next: string) => {
    setFolder(next);
    setPage(1);
    setSelected(null);
  };

  const data = list.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const folderOptions = folders.data && folders.data.length > 0 ? folders.data : [INBOX];

  return (
    <>
      <PageHeader
        title={t('owner_mail_title')}
        description={t('owner_mail_description')}
        actions={
          <Button size="sm" onClick={() => setDraft(EMPTY_DRAFT)}>
            <PenSquare className="h-3.5 w-3.5" /> {t('owner_mail_new_mail')}
          </Button>
        }
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-56" aria-label={t('owner_mail_folder')} value={folder} onChange={(e) => changeFolder(e.target.value)}>
            {folderOptions.map((entry) => (
              <option key={entry.path} value={entry.path}>
                {entry.name}
              </option>
            ))}
          </Select>
          <Button variant="outline" size="sm" disabled={refreshing} onClick={() => void refresh()}>
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} /> {t('owner_mail_refresh')}
          </Button>
          {data && (
            <span className="ms-auto text-xs text-muted-foreground">
              {t('owner_mail_count_read_at', { count: data.total, when: dateTime(data.fetchedAt) })}
            </span>
          )}
        </div>

        {list.isError && !data ? (
          <Alert variant="warning">
            <div className="space-y-2">
              <p>{describeError(list.error)}</p>
              <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
                {t('owner_mail_try_again')}
              </Button>
            </div>
          </Alert>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
            <Card>
              <CardContent className="space-y-1 p-2">
                {list.isLoading ? (
                  <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                    <Spinner /> {t('owner_mail_opening')}
                  </div>
                ) : data && data.messages.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">{t('owner_mail_empty_folder')}</p>
                ) : (
                  data?.messages.map((entry) => (
                    <button
                      key={entry.uid}
                      type="button"
                      onClick={() => setSelected(entry.uid)}
                      className={cn('block w-full rounded-md px-3 py-2 text-start transition-colors hover:bg-muted', selected === entry.uid && 'bg-primary/10')}
                    >
                      <span className="flex items-center gap-2">
                        {!entry.seen && <span aria-label={t('owner_mail_unread')} className="h-2 w-2 flex-none rounded-full bg-primary" />}
                        <span className={cn('truncate text-sm', entry.seen ? 'text-muted-foreground' : 'font-semibold')}>
                          {entry.from?.name ?? entry.from?.address ?? t('owner_mail_unknown_sender')}
                        </span>
                        {entry.hasAttachments && <Paperclip className="h-3 w-3 flex-none text-muted-foreground" aria-label={t('owner_mail_has_attachment')} />}
                        <span className="ms-auto flex-none text-[11px] text-muted-foreground">{entry.date ? dateTime(entry.date) : ''}</span>
                      </span>
                      <span className={cn('block truncate text-xs', entry.seen ? 'text-muted-foreground' : '')}>{entry.subject || t('owner_mail_no_subject')}</span>
                    </button>
                  ))
                )}
                {data && pages > 1 && (
                  <div className="flex items-center justify-between px-2 pt-2 text-xs text-muted-foreground">
                    <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      {t('owner_mail_newer')}
                    </Button>
                    <span>{t('owner_mail_page_of', { page, pages })}</span>
                    <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                      {t('owner_mail_older')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                {selected === null ? (
                  <p className="text-sm text-muted-foreground">{t('owner_mail_choose_message')}</p>
                ) : message.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner /> {t('owner_mail_opening')}
                  </div>
                ) : message.isError ? (
                  <p className="text-sm text-destructive">{describeError(message.error)}</p>
                ) : message.data ? (
                  <Reader message={message.data} onReply={reply} />
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {draft && <OwnerMailComposeDialog draft={draft} onClose={() => setDraft(null)} />}
    </>
  );
}
